/**
 * Admin: tenant-facturatie handmatig (eigen factuur — geen Stripe).
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

const STATUSES = [
  { value: 'active', label: 'Actief (licentie / factuur rond)' },
  { value: 'trial', label: 'Proefperiode' },
  { value: 'invoice_pending', label: 'Factuur verstuurd — wacht op betaling' },
  { value: 'past_due', label: 'Achterstallig' },
  { value: 'suspended', label: 'Gedeactiveerd' },
] as const;

export function AdminBilling() {
  const { user, refreshUser } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-billing', tenantId],
    queryFn: () => api.getTenantBilling(tenantId),
    enabled: !!user?.authenticated,
  });

  const [billingStatus, setBillingStatus] = useState('');
  const [trialEnds, setTrialEnds] = useState('');
  const [usageExports, setUsageExports] = useState(0);
  const [usageAnalyses, setUsageAnalyses] = useState(0);
  const [planLabel, setPlanLabel] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!data) return;
    setBillingStatus(data.billing_status);
    setTrialEnds(data.trial_ends_at ? data.trial_ends_at.slice(0, 16) : '');
    setUsageExports(data.usage_exports);
    setUsageAnalyses(data.usage_analyses);
    setPlanLabel(data.billing_plan_label ?? '');
    setNotes(data.billing_notes_internal ?? '');
  }, [data]);

  const mut = useMutation({
    mutationFn: () =>
      api.putTenantBilling(tenantId, {
        billing_status: billingStatus,
        trial_ends_at: trialEnds ? new Date(trialEnds).toISOString() : null,
        usage_exports: usageExports,
        usage_analyses: usageAnalyses,
        billing_plan_label: planLabel || null,
        billing_notes_internal: notes || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tenant-billing', tenantId] });
      await refreshUser();
    },
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');

  return (
    <div>
      <SeoMeta
        path="/admin/billing"
        noindex
        title="Facturatie tenant"
        description="Handmatige tenantfacturatie, licenties en proefperiode in CalqTool™ — platformbeheer."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Facturatie' }]} />
      <div className="mb-6 flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-primary" aria-hidden />
        <h1 className="text-2xl font-bold text-text">Facturatie (tenant)</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        Voor <strong className="text-text">self-serve abonnementen</strong> (Stripe Checkout en facturen via Stripe) zie{' '}
        <Link to="/account/billing" className="font-medium text-primary hover:underline">
          Account → Facturatie
        </Link>
        . Deze pagina blijft voor <strong className="text-text">handmatige</strong> tenantstatus en proef-tellers.
        Tenant <code className="rounded bg-surface px-1 text-xs">{tenantId}</code>.
      </p>

      {isLoading && <p className="text-text-muted">Laden…</p>}
      {error && (
        <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {errMsg.includes('beheerder') || errMsg.includes('403')
            ? 'Alleen beheerders zien deze pagina.'
            : errMsg}
        </p>
      )}

      {data && (
        <form
          className="max-w-xl space-y-4 rounded-xl border border-border bg-surface-card p-6 shadow-sm"
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Status</label>
            <select
              value={billingStatus}
              onChange={(e) => setBillingStatus(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Proefperiode tot (UTC, lokaal veld)</label>
            <input
              type="datetime-local"
              value={trialEnds}
              onChange={(e) => setTrialEnds(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-text-muted">Leeg laten = null in database bij opslaan (niet aanbevolen bij trial).</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Teller exports</label>
              <input
                type="number"
                min={0}
                value={usageExports}
                onChange={(e) => setUsageExports(Number(e.target.value) || 0)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Teller analyses</label>
              <input
                type="number"
                min={0}
                value={usageAnalyses}
                onChange={(e) => setUsageAnalyses(Number(e.target.value) || 0)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
          </div>
          <p className="text-xs text-text-muted">
            Projecten nu: <strong className="text-text">{data.project_count}</strong> (alleen ter info; niet hier te
            wijzigen).
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Planlabel (optioneel, zichtbaar voor admin)</label>
            <input
              type="text"
              value={planLabel}
              onChange={(e) => setPlanLabel(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              placeholder="bijv. Professional jaarlicentie"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text">Interne notities</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              placeholder="Factuurnummer, afspraken…"
            />
          </div>
          {mut.isError && (
            <p className="text-sm text-red-600">{mut.error instanceof Error ? mut.error.message : 'Opslaan mislukt'}</p>
          )}
          {mut.isSuccess && <p className="text-sm text-green-700 dark:text-green-400">Opgeslagen.</p>}
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {mut.isPending ? 'Opslaan…' : 'Opslaan'}
          </button>
        </form>
      )}
    </div>
  );
}
